import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDashboardBlockData, updateDashboardBlock } from '@/api/dashboards';
import { ChartBlock } from './blocks/ChartBlock';
import { ListBlock } from './blocks/ListBlock';
import { TextBlock } from './blocks/TextBlock';
import { BlockFilters } from './BlockFilters';
import { ConfigureBlockDialog } from './ConfigureBlockDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Settings2, Trash2 } from 'lucide-react';
import type {
  Dashboard,
  DashboardBlock as DashboardBlockType,
  DashboardBlockSize,
} from '@pm-next/shared-types';
import { gridToSize } from '@pm-next/shared-types';
import { cn } from '@/lib/utils';

interface DashboardBlockCardProps {
  dashboardId: string;
  block: DashboardBlockType;
  editMode: boolean;
  canEdit: boolean;
  onDeleted?: () => void;
}

export function DashboardBlockCard({
  dashboardId,
  block,
  editMode,
  canEdit,
  onDeleted,
}: DashboardBlockCardProps) {
  const [page, setPage] = useState(1);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState(block.title);
  const queryClient = useQueryClient();

  useEffect(() => {
    setTitleDraft(block.title);
  }, [block.title]);

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [
      'dashboard-block-data',
      dashboardId,
      block.id,
      page,
      JSON.stringify(block.toolArgs),
      JSON.stringify(block.config),
      block.visualization,
      block.chartType,
    ],
    queryFn: () => getDashboardBlockData(dashboardId, block.id, { page, pageSize: 10 }),
  });

  const updateBlock = useMutation({
    mutationFn: (body: Parameters<typeof updateDashboardBlock>[2]) =>
      updateDashboardBlock(dashboardId, block.id, body),
    onSuccess: (updatedBlock) => {
      queryClient.setQueryData<Dashboard>(['dashboard', dashboardId], (current) => {
        if (!current) return current;
        return {
          ...current,
          blocks: current.blocks?.map((item) =>
            item.id === updatedBlock.id ? updatedBlock : item
          ),
        };
      });
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { deleteDashboardBlock } = await import('@/api/dashboards');
      await deleteDashboardBlock(dashboardId, block.id);
    },
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
      onDeleted?.();
    },
  });

  const hideFilters = block.config?.ui?.hideFilters === true;
  const showFilters = block.filters.length > 0 && (editMode || !hideFilters);
  const isList = data?.visualization === 'list' || block.visualization === 'list';
  const isRefreshing = updateBlock.isPending || (isFetching && !isLoading);
  const chartConfig =
    block.config?.chart ?? data?.config?.chart ?? data?.chart?.chartConfig ?? null;

  const commitTitle = () => {
    const next = titleDraft.trim();
    if (!next || next === block.title) {
      setTitleDraft(block.title);
      return;
    }
    updateBlock.mutate({ title: next });
  };

  return (
    <div className="flex h-full w-full flex-col rounded-lg border bg-card p-3 shadow-sm">
      {/* Intrinsic-size probe used by the grid to equalize row heights without stretch feedback. */}
      <div data-dashboard-block-measure className="w-full shrink-0">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-[120px] flex-1">
            {editMode && canEdit ? (
              <Input
                className="h-8 text-sm font-semibold"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
              />
            ) : (
              <h3 className="truncate text-sm font-semibold">{block.title}</h3>
            )}
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-1">
            {showFilters && (
              <BlockFilters
                filters={block.filters}
                toolArgs={block.toolArgs}
                disabled={!canEdit || updateBlock.isPending}
                onChange={(toolArgs) => updateBlock.mutate({ toolArgs })}
              />
            )}
            {isRefreshing && (
              <Loader2
                className="h-4 w-4 animate-spin text-muted-foreground"
                aria-label="Refreshing"
              />
            )}
            {editMode && canEdit && (
              <>
                <Select
                  value={gridToSize(block.w)}
                  onValueChange={(v) => updateBlock.mutate({ size: v as DashboardBlockSize })}
                >
                  <SelectTrigger className="h-8 w-[88px] text-xs">
                    <SelectValue placeholder="Size" />
                  </SelectTrigger>
                  <SelectContent>
                    {(['sm', 'md', 'lg', 'full'] as DashboardBlockSize[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setConfigureOpen(true)}
                  title="Configure block"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => setDeleteOpen(true)}
                  disabled={remove.isPending}
                  title="Delete block"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        <div
          className={cn('relative', isList ? 'max-h-[320px] overflow-auto' : 'overflow-visible')}
        >
          {isLoading && <Skeleton className="h-48 w-full rounded-md" />}
          {isError && (
            <p className="text-sm text-destructive">
              {(error as Error)?.message || 'Failed to load block data'}
            </p>
          )}
          {data?.error && <p className="text-sm text-destructive">{data.error}</p>}
          {data && !data.error && data.visualization === 'chart' && data.chart && (
            <ChartBlock data={data.chart} chartConfig={chartConfig} />
          )}
          {data && !data.error && data.visualization === 'list' && data.list && (
            <ListBlock data={data.list} onPageChange={setPage} />
          )}
          {data && !data.error && data.visualization === 'text' && data.text && (
            <TextBlock
              data={data.text}
              layout={block.config?.text?.layout ?? data.config?.text?.layout ?? data.text.layout}
              showHeadline={false}
              blockTitle={block.title}
            />
          )}
          {isRefreshing && !isLoading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-card/60"
              aria-busy="true"
              aria-live="polite"
            >
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {/* Absorbs extra cell height so cards in a row share a common border box. */}
      <div className="min-h-0 flex-1" aria-hidden />

      <ConfigureBlockDialog
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        dashboardId={dashboardId}
        block={block}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete block?</DialogTitle>
            <DialogDescription>
              This removes &quot;{block.title}&quot; from the dashboard. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Delete block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
